import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { renderToString } from 'react-dom/server';
import HealthChartTooltip from './tooltip/HealthChartTooltip';
import { TimezoneUtils } from '@common/utils';

const BASE_HEIGHT = 60; 
const ROW_HEIGHT = 60; 
const MIN_BAR_WIDTH = 60; 

const HEALTH_COLORS = {
  success: '#78dd7b',
  warning: '#ff9f43',
  danger: '#ea5455',
  none: '#e0e0e0'
};

export function HealthChart({ metrics = [], store = {} }) {
  const chartRef = useRef(null);

  const { seriesData, categories, chartHeight, maxValue } = useMemo(() => {
    const competitorMap = store?.competitors?.reduce((acc, competitor) => {
      acc[competitor.id] = competitor.name;
      return acc;
    }, {});

    const data = [...metrics].reverse()
      .map(item => {
        const isMyStore = item.competitorId == store?.id;
        const competitorName = isMyStore 
          ? 'Mi tienda' 
          : (competitorMap[item.competitorId] || `Competidor ${item.competitorId}`);
        return {
          ...item,
          name: competitorName,
          color: HEALTH_COLORS[item.indicator] || HEALTH_COLORS.none,
          customData: {
            name: competitorName,
            totalProducts: item.totalProducts,
            updatedProducts: item.updatedProducts,
            percentage: item.percentage,
            lastUpdate: TimezoneUtils.formatDateWithCountryCode(item.lastUpdate, store?.country),
            color: HEALTH_COLORS[item.indicator] || HEALTH_COLORS.none
          }
        };
      });

    const cats = data.map(item => item.name);
    const maxVal = Math.max(...data.map(d => d.totalProducts), 0);
    const height = BASE_HEIGHT + (data.length * ROW_HEIGHT);

    return { seriesData: data, categories: cats, chartHeight: height, maxValue: maxVal };
  }, [metrics, store]);

  const options = {
    chart: {
      type: 'bar',
      height: chartHeight,
      backgroundColor: 'transparent',
      style: { fontFamily: 'sans-serif' }
    },
    title: { text: '' },
    xAxis: {
      categories: categories,
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        style: {
          color: '#555',
          fontSize: '13px',
          fontWeight: '500'
        }
      }
    },
    yAxis: {
      min: 0,
      max: maxValue,
      tickAmount: 5,
      title: { text: '' },
      gridLineWidth: 1,
      gridLineColor: '#e6e6e6',
      labels: {
        style: { color: '#999', fontSize: '11px' },
        y: 20,
        formatter: function() {
            const val = this.value;
            if (val === 0) return "0";
            if (val >= 1000) return `${val / 1000}K`;
            return val.toString();
        }
      }
    },
    legend: { enabled: false },
    
    plotOptions: {
      series: {
        grouping: false,
        borderWidth: 0,
        shadow: false,
        animation: false,
        
        pointPadding: 0, 
        
        groupPadding: 0.15, 
        
        minPointLength: MIN_BAR_WIDTH
      }
    },
    
    tooltip: {
      enabled: true,
      useHTML: true,
      backgroundColor: 'rgba(255,255,255,0)',
      borderWidth: 0,
      borderRadius: 0,
      shadow: false,
      headerFormat: '',
      padding: 0,
      outside: true,
      formatter: function() {
        const point = this.point;
        if (!point || !point.customData) return '';
        
            return renderToString(
                <HealthChartTooltip 
                    name={point.customData.name}
                    lastUpdate={point.customData.lastUpdate}
                    percentage={point.customData.percentage}
                    updatedProducts={point.customData.updatedProducts}
                    totalProducts={point.customData.totalProducts}
                    color={point.customData.color}
                />
            );
        }
    },
    series: [
      {
        name: 'Total',
        data: seriesData.map(item => ({
          y: item.totalProducts,
          customData: item.customData
        })),
        color: '#d8d8d8',
        enableMouseTracking: true,
        dataLabels: { enabled: false },
        zIndex: 0
      },
      {
        name: 'Progreso',
        data: seriesData.map(item => ({
          y: item.updatedProducts,
          color: item.color,
          customData: item.customData
        })),
        zIndex: 1,
        dataLabels: {
          enabled: true,
          inside: true,
          align: 'right',
          format: '{point.customData.percentage}%',
          style: {
            color: '#fff',
            textOutline: 'none',
            fontSize: '13px',
            fontWeight: 600
          },
          x: -10
        }
      }
    ]
  };

  return (
    <div data-testid="health-chart" className="health-chart" style={{ width: '100%', height: '100%'}}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}

HealthChart.propTypes = {
  metrics: PropTypes.array,
  store: PropTypes.object
};

export default HealthChart;